import { useMutation, useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";

import { get } from "@/api";
import { Text } from "@/atoms";
import { Button } from "@/atoms/button/Button";
import RepeatCash from "@/atoms/icon/icons/RepeatCash.svg";
import { AnimatedBackground } from "@/components/AnimatedBackground/AnimatedBackground";
import { PlatformLogo } from "@/components/PlatformLogo";
import { useCountUp } from "@/hooks";
import {
  acknowledgeGiftCardTransferById,
  getNotAcknowledgedGiftCards,
  GiftCardPreviewImage,
  refreshNotAcknowledgedGiftCardsQuery,
} from "@/modules/giftCards";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";

export const GiftCardAcknowledgeModal = () => {
  const [showModal, setShowModal] = useState(false);

  const { data: platform } = useQuery({
    queryKey: ["/platform"],
    queryFn: () => get("/platform"),
  });

  const { data: notAcknowledgedGiftCards = [], isLoading } = useQuery({
    queryKey: getNotAcknowledgedGiftCards.keyed().queryKey,
    queryFn: () => getNotAcknowledgedGiftCards(),
  });

  const acknowledgeMutation = useMutation({
    mutationFn: async (transferIds: string[]) => {
      await Promise.all(
        transferIds.map((id) =>
          acknowledgeGiftCardTransferById({ transferId: id })
        )
      );
    },
    onSuccess: () => {
      refreshNotAcknowledgedGiftCardsQuery();
    },
    onError: (error: Error) => {
      console.log("error", error);
    },
  });

  useEffect(() => {
    if (notAcknowledgedGiftCards.length > 0 && !isLoading) {
      setShowModal(true);
      document.body.classList.add("overflow-hidden");
    } else if (notAcknowledgedGiftCards.length === 0 && showModal) {
      setShowModal(false);
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [notAcknowledgedGiftCards, isLoading, showModal]);

  const handleAcknowledge = () => {
    const transferIds = notAcknowledgedGiftCards.map((gc) => gc.id);
    acknowledgeMutation.mutate(transferIds);
  };

  const giftCardSum = notAcknowledgedGiftCards?.reduce(
    (acc, giftCard) => acc + giftCard.giftCardItemAmountInCents / 100,
    0
  );

  const value = useCountUp({
    to: giftCardSum,
    from: 0,
    duration: 1500,
    delay: 250,
  });

  const getGiftCardTitle = () => {
    if (notAcknowledgedGiftCards.length === 1) {
      return "Gift Card Received!";
    }
    return `${notAcknowledgedGiftCards.length} Gift Cards Received!`;
  };

  // never actually acknowledges the gift cards, just closes the modal
  // basically escape hatch
  const handleClose = () => {
    setShowModal(false);
  };

  const renderHeader = () => (
    <div className="flex items-center justify-end px-4 py-2">
      <button
        onClick={handleClose}
        className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
        aria-label="Close modal"
      >
        <X size={24} />
      </button>
    </div>
  );

  const renderAmountDisplay = () => (
    <motion.div
      className="mt-6 flex items-center gap-4 rounded-full bg-white/20 px-6 py-4 shadow-sm"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.4 }}
    >
      <RepeatCash className="h-12 w-12" />
      <Text text={`$${value}`} variant="v3-h2" />
    </motion.div>
  );

  const renderSingleGiftCard = () => {
    const giftCard = notAcknowledgedGiftCards[0];
    const amountInDollars = giftCard.giftCardItemAmountInCents / 100;

    return (
      <div className="flex flex-1 flex-col items-center justify-center px-8">
        <motion.div
          key={giftCard.id}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          className="flex w-full flex-col items-center gap-4 text-center"
        >
          <PlatformLogo
            logoUrl={platform?.logoImageUrl}
            platformName={platform?.name}
            className="h-16 w-auto max-w-[200px]"
          />

          <Text text={getGiftCardTitle()} variant="v3-h2" />

          {/* Gift Card Preview */}
          <motion.div
            className="relative mt-4 w-full"
            style={{ aspectRatio: "1.586" }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <div className="relative h-full w-full overflow-hidden rounded-2xl shadow-2xl">
              <GiftCardPreviewImage
                backgroundImage={giftCard.imageUrl || ""}
                amount={amountInDollars.toString()}
                size="large"
                platformLogoUrl={platform?.logoImageUrl}
              />
            </div>
          </motion.div>

          {renderAmountDisplay()}
        </motion.div>
      </div>
    );
  };

  const renderMultipleGiftCards = () => {
    return (
      <div className="flex w-full flex-1 flex-col items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          className="flex w-full flex-col items-center gap-4 text-center"
        >
          <PlatformLogo
            logoUrl={platform?.logoImageUrl}
            platformName={platform?.name}
            className="h-16 w-auto max-w-[200px]"
          />

          <Text text={getGiftCardTitle()} variant="v3-h2" />

          <motion.div
            className="relative mt-4 w-full"
            style={{ aspectRatio: "1.586" }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <Swiper
              className="!px-8"
              slidesPerView="auto"
              spaceBetween={24} // 24px = gap-6
              freeMode={true}
              grabCursor={true}
            >
              {notAcknowledgedGiftCards.map((listing) => {
                return (
                  <SwiperSlide
                    key={listing.id}
                    className="!w-auto cursor-pointer pb-4"
                  >
                    <div className="h-[200px] w-[300px] overflow-hidden rounded-xl shadow-md">
                      <GiftCardPreviewImage
                        backgroundImage={listing.imageUrl as string}
                        amount={(
                          listing.giftCardItemAmountInCents / 100
                        ).toString()}
                        size="large"
                      />
                    </div>
                    <Text
                      text={`From: ${listing.senderInformation}`}
                      variant="body1"
                      className="pt-3"
                      component="p"
                    />
                    <Text
                      text={listing.personalMessage}
                      variant="body1"
                      className="pt-3"
                    />
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </motion.div>

          {renderAmountDisplay()}
        </motion.div>
      </div>
    );
  };

  const renderFooter = () => {
    const buttonText = acknowledgeMutation.isLoading
      ? "Processing..."
      : notAcknowledgedGiftCards.length > 1
      ? "Bank them"
      : "Bank it";

    return (
      <Button
        fullWidth
        size="large"
        onClick={handleAcknowledge}
        disabled={acknowledgeMutation.isLoading}
      >
        {buttonText}
      </Button>
    );
  };

  const renderContent = () => {
    if (!notAcknowledgedGiftCards.length)
      return (
        <div className="flex min-h-[50vh] flex-1 items-center justify-center text-center text-2xl font-bold">
          No gift cards to acknowledge
        </div>
      );

    return notAcknowledgedGiftCards.length === 1
      ? renderSingleGiftCard()
      : renderMultipleGiftCards();
  };

  return (
    <AnimatePresence>
      {showModal && (
        <motion.div
          className="flexh-full fixed inset-0 z-50 mx-auto  min-h-screen w-full flex-col items-center justify-center sm:max-w-[390px]"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <MobileViewportContainer
            header={renderHeader()}
            bottomCta={renderFooter()}
            contentClassName="pb-4 pt-0 !px-0"
            showAnimatedBackground={true}
          >
            {renderContent()}
          </MobileViewportContainer>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
